import { Grid } from '@chakra-ui/react'
import type { CategoryStats, Statistics } from 'BrokerTsLib/src/dtos'
import { useMemo } from 'react'

import { CircularProgressWithStats } from '@/components/CircularProgress'
import { DecompositionChart } from '@/components/DecompositionChart'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { ChartCard } from './ChartCard'

interface ChartsGridProps {
  stats: Statistics
  adjustedPreviousStats: Statistics
  isLoadingStats: boolean
  anyData: boolean
  previousTimeRangeLabel: string
  showCategories?: boolean
}

const GetVehicleCategory = ({ category, total }: CategoryStats) => category == 1 ? {
  name: 'POV',
  value: total,
  fill: 'var(--ce-colors-green-500)',
} : category == 2 ? {
  name: 'HAV',
  value: total,
  fill: 'var(--ce-colors-blue-500)',
} : {
  name: 'Doplňky',
  value: total,
  fill: 'var(--ce-colors-orange-300)',
}

export const ChartsGrid = ({
  stats,
  adjustedPreviousStats,
  isLoadingStats,
  anyData,
  previousTimeRangeLabel,
  showCategories
}: ChartsGridProps) => {
  const anyCategories = !!((stats.categories?.length ?? 0) > 0 && showCategories)

  const { translate } = useTranslationsContext()

  const categoryStats = useMemo(() =>
    (anyCategories ? [
      ...stats.categories.map(GetVehicleCategory),
      {
        name: translate('other', 'Ostatní'),
        value: stats.predepanePojistne - stats.categories.reduce((acc, category) => acc + category.total, 0),
        fill: 'var(--ce-colors-gray-100)',
      }
    ] : []).filter(category => category.value > 0).sort((a, b) => b.value - a.value)
  , [anyCategories, stats.predepanePojistne])

  const { app } = useApp()

  return (
    <Grid sx={{ gap: 0, gridTemplateColumns: anyCategories ? '1fr 1fr' : 'auto' }}>
      {app !== Apps.leasing &&
        <ChartCard label={translate('ui.Dashboard.Stats.Conversion', 'Konverze')}>
          <CircularProgressWithStats
            isLoading={isLoadingStats}
            value={anyData ? undefined : Math.min(stats.pocetSmluv / stats.pocetVytvorenych, 1) * 100}
            valueUnit={anyData ? 'N/A' : '%'}
            previousValue={anyData ? undefined : Math.round(Math.min((adjustedPreviousStats.pocetSmluv / adjustedPreviousStats.pocetVytvorenych), 1) * 100)}
            previousValueLabel={anyData ? undefined : previousTimeRangeLabel}
            color='stats-conversion-color'
          />
        </ChartCard>
      }

      {anyCategories &&
        <ChartCard label={translate('ui.Dashboard.Stats.Products', 'Produkty')}>
          <DecompositionChart
            isLoading={isLoadingStats}
            height='120px'
            width='120px'
            data={categoryStats}
          />
        </ChartCard>}
    </Grid>
  )
}
